import { PromoListItem } from "@/core/entities/promotion/PromoListItem";
import { PromoDetail } from "@/core/entities/promotion/PromoDetail";
import { IUpdatePromoProgramParams, ICreatePromoProgramParams, IPromoDetail } from "@/core/repositories/promotion/IPromoDetail.repository";
import { PromoStatusTransitionType } from "@/core/enums/PromoStatusTransitionType";
import { GetPromoDetailUseCase } from "@/core/usecases/promotion/GetPromoDetailUseCase";
import { PutPromoStatusUseCase } from "@/core/usecases/promotion/PutPromoStatusUseCase";
import { UpdatePromoProgramUseCase } from "@/core/usecases/promotion/UpdatePromoProgramUseCase";
import { CreatePromoProgramUseCase } from "@/core/usecases/promotion/CreatePromoProgramUseCase";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl/GraphQl.adapter";
import { PromoDetailRepository } from "@/infrastructure/repositories/promotion/PromoDetail.repository";
import { atom } from "jotai";
import { promotionDrawerInfoAtom } from "./promotion.atoms";
import { PromoProgramEditMode, promoDetailAtom } from "./promoDetail.atoms";

const client = GraphQlAdapter.getInstance().getClient();
const promoDetailRepository = new PromoDetailRepository(client);

const mergeWithListItemFallback = (detail: IPromoDetail, listItem: PromoListItem | null): IPromoDetail => {
  if (!listItem) {
    return detail;
  }

  return {
    ...detail,
    startDate: detail.startDate || listItem.promoStartDate,
    endDate: detail.endDate || listItem.promoEndDate,
    type: {
      promoTypeId: detail.type.promoTypeId || listItem.type.promoTypeId,
      promoTypeName: detail.type.promoTypeName || listItem.type.promoTypeName,
      promoTypeCode: detail.type.promoTypeCode || listItem.type.promoTypeCode,
      promoTypeKey: detail.type.promoTypeKey || listItem.type.promoTypeKey,
    },
    status: {
      promoStatusId: detail.status.promoStatusId || listItem.status.promoStatusId,
      promoStatusName: detail.status.promoStatusName || listItem.status.promoStatusName,
      promoStatusKey: detail.status.promoStatusKey || listItem.status.promoStatusKey,
    },
    purchaser: {
      ...detail.purchaser,
      purchaserName: detail.purchaser.purchaserName || listItem.purchaserName || "",
      purchaserKey: detail.purchaser.purchaserKey || "",
    },
    retailer: {
      retailerId: detail.retailer?.retailerId ?? null,
      retailerKey: detail.retailer?.retailerKey ?? null,
      retailerName: detail.retailer?.retailerName ?? listItem.retailerName,
    },
  };
};

export const getPromoDetailAtom = atom(null, async (get, set, refetch?: boolean) => {
  const shouldRefetch = refetch === true;
  const drawerInfo = get(promotionDrawerInfoAtom);
  const promoEventId = drawerInfo.promoEventId;

  if (!promoEventId) {
    return;
  }

  set(promoDetailAtom, {
    ...get(promoDetailAtom),
    isLoading: true,
    error: null,
  });

  try {
    const result = await new GetPromoDetailUseCase(promoDetailRepository).execute({ promoEventId }, shouldRefetch);
    const merged = mergeWithListItemFallback(result, drawerInfo.promoItem ?? null);
    set(promoDetailAtom, {
      ...get(promoDetailAtom),
      promoDetail: new PromoDetail(merged),
      isLoading: false,
      error: null,
    });
  } catch (error) {
    set(promoDetailAtom, {
      ...get(promoDetailAtom),
      isLoading: false,
      error: error as Error,
    });
  }
});

export const resetPromoDetailAtom = atom(null, (_get, set) => {
  set(promoDetailAtom, {
    promoDetail: null,
    isLoading: false,
    isTransitioning: false,
    isUpdating: false,
    isCreating: false,
    editMode: null,
    error: null,
  });
});

export const setPromoProgramEditModeAtom = atom(null, (get, set, editMode: PromoProgramEditMode | null) => {
  set(promoDetailAtom, {
    ...get(promoDetailAtom),
    editMode,
  });
});

export interface PutPromoStatusResult {
  detail: PromoDetail;
  type: PromoStatusTransitionType;
}

export const putPromoStatusAtom = atom(
  null,
  async (get, set, type: PromoStatusTransitionType): Promise<PutPromoStatusResult> => {
    const drawerInfo = get(promotionDrawerInfoAtom);
    const promoEventId = drawerInfo.promoEventId;

    if (!promoEventId) {
      throw new Error("Promotion ID is required");
    }

    set(promoDetailAtom, {
      ...get(promoDetailAtom),
      isTransitioning: true,
      error: null,
    });

    try {
      const result = await new PutPromoStatusUseCase(promoDetailRepository).execute({
        promoEventId,
        type,
      });
      const merged = mergeWithListItemFallback(result, drawerInfo.promoItem ?? null);
      const detail = new PromoDetail(merged);
      set(promoDetailAtom, {
        ...get(promoDetailAtom),
        promoDetail: detail,
        isLoading: false,
        isTransitioning: false,
        editMode: null,
        error: null,
      });
      return { detail, type };
    } catch (error) {
      set(promoDetailAtom, {
        ...get(promoDetailAtom),
        isTransitioning: false,
        error: error as Error,
      });
      throw error;
    }
  }
);

export const updatePromoProgramAtom = atom(
  null,
  async (get, set, params: IUpdatePromoProgramParams): Promise<PromoDetail> => {
    const drawerInfo = get(promotionDrawerInfoAtom);

    set(promoDetailAtom, {
      ...get(promoDetailAtom),
      isUpdating: true,
      error: null,
    });

    try {
      const result = await new UpdatePromoProgramUseCase(promoDetailRepository).execute(params);
      const merged = mergeWithListItemFallback(result, drawerInfo.promoItem ?? null);
      const detail = new PromoDetail(merged);
      set(promoDetailAtom, {
        ...get(promoDetailAtom),
        promoDetail: detail,
        isLoading: false,
        isUpdating: false,
        editMode: null,
        error: null,
      });
      return detail;
    } catch (error) {
      set(promoDetailAtom, {
        ...get(promoDetailAtom),
        isUpdating: false,
        error: error as Error,
      });
      throw error;
    }
  }
);

export const createPromoProgramAtom = atom(
  null,
  async (get, set, params: ICreatePromoProgramParams): Promise<PromoDetail> => {
    set(promoDetailAtom, {
      ...get(promoDetailAtom),
      isCreating: true,
      error: null,
    });

    try {
      const result = await new CreatePromoProgramUseCase(promoDetailRepository).execute(params);
      const detail = new PromoDetail(result);
      set(promoDetailAtom, {
        ...get(promoDetailAtom),
        promoDetail: detail,
        isCreating: false,
        error: null,
      });
      return detail;
    } catch (error) {
      set(promoDetailAtom, {
        ...get(promoDetailAtom),
        isCreating: false,
        error: error as Error,
      });
      throw error;
    }
  }
);
